import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import {
  getFieldLabel,
  getFieldLabels,
  getColumnHeaderHintsForDisplay,
  OPTIONAL_IMPORT_FIELDS,
  REQUIRED_FIELDS,
} from '../../../lib/utils/csvParser';

const TEMPLATE_FIELD_ORDER = [
  'first_name',
  'last_name',
  'linkedin_url',
  'current_company',
  'current_position',
  'location',
  'email',
  'phone',
];

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const ColumnListItem = ({ field }) => {
  const { t } = useTranslation();
  const hints = getColumnHeaderHintsForDisplay(field);

  return (
    <li>
      <span className="text-muted-foreground">• {getFieldLabel(field)}</span>
      {hints.length > 0 && (
        <p className="text-xs text-muted-foreground/80 mt-0.5 pl-3 leading-relaxed">
          {t('import.template.orAliases', { aliases: hints.join(', ') })}
        </p>
      )}
    </li>
  );
};

const TemplateDownload = () => {
  const { t } = useTranslation();

  const handleDownloadTemplate = () => {
    const labels = getFieldLabels();
    const headers = TEMPLATE_FIELD_ORDER.map((field) => labels[field]);
    const csvContent = [
      headers,
      ['John', 'Doe', 'https://linkedin.com/in/johndoe', 'Tech Corp', 'Software Engineer', 'Amsterdam', 'john@example.com', '+31612345678'],
      ['Jane', 'Smith', 'https://linkedin.com/in/janesmith', 'Innovation Labs', 'Product Manager', 'Rotterdam', 'jane@example.com', '+31687654321'],
    ].map((row) => row.map(escapeCsvCell).join(',')).join('\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', t('import.template.fileName'));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-foreground mb-2">{t('import.template.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('import.template.description')}
          </p>
        </div>
        <Button
          onClick={handleDownloadTemplate}
          iconName="Download"
          variant="outline"
          size="lg"
          className="sm:w-auto w-full"
        >
          {t('import.template.download')}
        </Button>
      </div>

      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground mb-4">
          {t('import.template.matchingHint')}
        </p>

        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-foreground mb-2 flex items-center gap-2">
              <Icon name="CheckCircle2" size={16} className="text-success" />
              {t('import.template.mandatory')}
            </p>
            <ul className="space-y-2 ml-3">
              {REQUIRED_FIELDS.map((field) => (
                <ColumnListItem key={field} field={field} />
              ))}
            </ul>
          </div>
          <div>
            <p className="font-medium text-foreground mb-2 flex items-center gap-2">
              <Icon name="Circle" size={16} className="text-muted-foreground" />
              {t('import.template.optional')}
            </p>
            <ul className="space-y-2 ml-3">
              {OPTIONAL_IMPORT_FIELDS.map((field) => (
                <ColumnListItem key={field} field={field} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateDownload;
